/**
 * @file        src/components/PendingActions.jsx
 * @description The Dashboard's "Needs action" panel: every open queue this
 *              viewer is allowed to see, counted, worst-waiting first.
 *
 *              TWO BLOCKS, NOT ONE TOTAL. This screen's audience sits above a
 *              separately headed "Whole business" block, because four of the
 *              twelve queues belong to no audience at all — payment
 *              discrepancies, abandoned checkouts, enquiries and group booking
 *              requests are mostly guest submissions, which is exactly why
 *              Transaction Log, Abandoned Payments and Payment Reconciliation
 *              are whole-business screens
 *              (§9d/§16). Folding them into one headline would count the same
 *              item under BOTH dashboards and break the partition §9d's
 *              self-test asserts. Cash Balance is the standing precedent: show
 *              the whole-business figure, and label it as one.
 *
 *              THE COUNTS COME FROM THE SAME QUERY THE PAGE LISTS. A tile that
 *              says "7 open" above a page that lists five is the Booking List
 *              summary bug (§9d) rebuilt, so the panel never derives a number of
 *              its own — actionCenter.repo answers both.
 *
 *              "N need you" EXCLUDES THE SUPPLIER-OWNED WORK IT STILL SHOWS.
 *              A cancellation the airline has not answered is real, waiting work
 *              and is counted and listed; it is not work anybody in this console
 *              can do today, so it stays out of the headline that says how much
 *              is on YOUR desk.
 * @author      Kinetika Labs
 * @created     2026-09-24
 * @changelog
 *   - 2026-09-24 Claude — Initial.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Icon from './Icon';
import Chip from './Chip';
import { qk } from '../api/queryKeys';
import { actionCenterOverview } from '../api/actionCenter';
import { targetFor } from '../lib/actionTargets';
import { inrCompact } from '../lib/format';

/** Whole days since `iso`, or null when nothing is waiting. */
function waitingDays(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 0;
  return Math.floor(ms / 86_400_000);
}

function QueueRow({ q }) {
  const t = targetFor(q.key);
  const days = waitingDays(q.oldest);
  return (
    <Link className="pa-row" to={`/account/action-center?kind=${q.key}`}>
      <span className={`s-ic ${t.tone}`}><Icon name={t.icon} /></span>
      <span className="pa-main">
        <b>{q.label}</b>
        <small>
          {q.owner === 'supplier' ? 'Waiting on the supplier' : 'Waiting on us'}
          {q.amount ? ` · ${inrCompact(q.amount)}` : ''}
          {days !== null ? ` · oldest ${days === 0 ? 'today' : `${days}d`}` : ''}
        </small>
      </span>
      <span className="s-val">{q.count}</span>
    </Link>
  );
}

function Block({ heading, note, queues }) {
  const open = queues.filter((q) => q.count > 0);
  if (!queues.length) return null;
  return (
    <div className="pa-block">
      <div className="pa-block-head">
        <h3>{heading}</h3>
        {note ? <p className="muted">{note}</p> : null}
      </div>
      {open.length === 0
        ? <p className="muted pa-clear"><Icon name="check" size={14} /> Nothing open.</p>
        : <div className="stat-list">{open.map((q) => <QueueRow key={q.key} q={q} />)}</div>}
    </div>
  );
}

/**
 * @param {'b2c'|'b2b'} audience  The SCREEN's audience — fixed by the route, not
 *        chosen here (see AudienceTabs).
 */
export default function PendingActions({ audience }) {
  const { data, isLoading } = useQuery({
    queryKey: qk.actionCenter.overview(audience),
    queryFn: () => actionCenterOverview({ audience }).then((r) => r.data),
    staleTime: 30_000,
  });

  const audienceQueues = data?.audienceQueues || [];
  const wholeQueues = data?.wholeBusinessQueues || [];
  const actionable = data?.totals?.actionable ?? null;

  return (
    <article className="panel glass pending-actions">
      <div className="panel-head">
        <div>
          <h2>Needs action</h2>
          <p className="muted">
            {isLoading ? 'Checking every queue…'
              : actionable === 0 ? 'Nothing is waiting on you.'
                : `${actionable} item${actionable === 1 ? '' : 's'} need you`}
          </p>
        </div>
        <Link className="btn ghost sm" to="/account/action-center">
          Action Centre <Icon name="chev" size={14} />
        </Link>
      </div>

      {isLoading ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <Block
            heading={audience === 'b2b' ? 'Corporate' : 'Retail'}
            queues={audienceQueues}
          />
          <Block
            heading="Whole business"
            /* Said out loud for the same reason the Cash Balance tile is
               labelled "(all business)": these three queues appear identically
               on BOTH dashboards, and a reader who assumed they were this
               screen's audience would double-count them. */
            note="Counted the same on both dashboards — these queues have no customer type."
            queues={wholeQueues}
          />
          {audienceQueues.length === 0 && wholeQueues.length === 0 && (
            <p className="muted">No queues are assigned to your account.</p>
          )}
        </>
      )}
    </article>
  );
}
